import { SPRING_DEFAULTS } from "../spring";

import {
  definePreset,
  DIRECTION_SCHEMA,
  DISTANCE_SCHEMA,
  kf,
  rad,
  samplePath,
  SLIDE_VECTORS,
  timing,
} from "./helpers";

import type { AnimationPreset } from "./types";

export const physicsPresets: AnimationPreset[] = [
  definePreset(
    "drop",
    "Drop",
    "physics",
    {
      params: { distance: 200 },
      schema: [DISTANCE_SCHEMA],
      easing: "bounce.out",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { dy: -(cfg.params.distance ?? 200) },
        { dy: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "fall",
    "Fall",
    "physics",
    {
      params: { distance: 300, tilt: 15 },
      schema: [
        DISTANCE_SCHEMA,
        { key: "tilt", label: "Tilt °", min: 0, max: 90, step: 1 },
      ],
      easing: "power2.in",
    },
    (tl, target, _element, cfg) => {
      tl.to(
        target,
        {
          dy: cfg.params.distance ?? 300,
          angleDelta: rad(cfg.params.tilt ?? 15),
          opacity: 0,
          ...timing(cfg),
        },
        0,
      );
    },
  ),
  definePreset(
    "pendulum",
    "Pendulum",
    "physics",
    {
      params: { degrees: 20 },
      schema: [{ key: "degrees", label: "Degrees", min: 1, max: 90, step: 1 }],
      repeat: 2,
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 20;
      tl.to(
        target,
        kf(cfg, { angleDelta: [0, rad(d), 0, rad(-d), 0] }, "sine.inOut"),
        0,
      );
    },
  ),
  definePreset(
    "spring",
    "Spring",
    "physics",
    {
      params: { distance: 100 },
      schema: [DISTANCE_SCHEMA],
      // real damped-oscillator physics — the runtime substitutes the solver's
      // ease and settling duration (spring.ts), so easing/duration here are
      // only the fallback if a user clears the spring in settings
      easing: "elastic.out",
      spring: SPRING_DEFAULTS,
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { dx: -(cfg.params.distance ?? 100) },
        { dx: 0, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "springDrop",
    "Spring Drop",
    "physics",
    {
      params: { distance: 200 },
      schema: [DISTANCE_SCHEMA],
      easing: "power2.out",
      spring: { ...SPRING_DEFAULTS, stiffness: 180, damping: 12 },
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { dy: -(cfg.params.distance ?? 200), opacity: 0.4 },
        { dy: 0, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "float",
    "Float",
    "physics",
    {
      params: { amplitude: 12 },
      schema: [
        { key: "amplitude", label: "Amplitude", min: 1, max: 100, step: 1 },
      ],
      duration: 2000,
      repeat: 2,
    },
    (tl, target, _element, cfg) => {
      const a = cfg.params.amplitude ?? 12;
      tl.to(target, kf(cfg, { dy: [0, -a, 0] }, "sine.inOut"), 0);
    },
  ),
  definePreset(
    "glide",
    "Glide",
    "physics",
    {
      params: { direction: 1, distance: 150 },
      schema: [DIRECTION_SCHEMA, DISTANCE_SCHEMA],
      easing: "power4.out",
    },
    (tl, target, _element, cfg) => {
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 1] ?? SLIDE_VECTORS[1];
      const d = cfg.params.distance ?? 150;
      tl.to(target, { dx: vx * d, dy: vy * d, ...timing(cfg) }, 0);
    },
  ),
  definePreset(
    "toss",
    "Toss",
    "physics",
    {
      params: { direction: 0, distance: 240, height: 120, degrees: 180 },
      schema: [
        {
          key: "direction",
          label: "From side",
          min: 0,
          max: 1,
          step: 1,
          options: ["Left", "Right"],
        },
        { key: "distance", label: "Distance px", min: 50, max: 1000, step: 10 },
        { key: "height", label: "Arc height px", min: 20, max: 500, step: 10 },
        { key: "degrees", label: "Tumble °", min: -720, max: 720, step: 15 },
      ],
      duration: 1200,
      easing: "none",
    },
    // ballistic toss into place: a true parabola — enters from the side,
    // rises to `height` mid-flight, lands at rest — with a tumble that
    // unwinds to the element's natural angle. The sampled path carries the
    // motion shape, so the default ease is linear.
    (tl, target, _element, cfg) => {
      const sign = (cfg.params.direction ?? 0) === 0 ? -1 : 1;
      const d = (cfg.params.distance ?? 240) * sign;
      const h = cfg.params.height ?? 120;
      const frames = samplePath((t) => [d * (1 - t), -4 * h * t * (1 - t)]);
      tl.to(
        target,
        kf(
          cfg,
          {
            ...frames,
            angleDelta: [rad(-(cfg.params.degrees ?? 180)) * sign, 0],
            opacity: [0, 1],
          },
          "none",
        ),
        0,
      );
    },
  ),
];
